import {decrementIssuesTabCounter, incrementIssuesTabCounter} from '@github-ui/issues-tab-counter'
import {adjustOpenClosedCounts} from '@github-ui/issues-tab-counter/open-closed-count'
import {
  type DeltaEntityFieldValuesOperation,
  DOCUMENT_FIELDS_PATH,
  type SetFieldsOperation,
} from '@github-ui/optimistic-connection'
import {ENTITY_FIELDS_OWNER, entityFieldsMutationKey} from '@github-ui/optimistic-connection/pendingOperations'
import {notifySettlement} from '@github-ui/optimistic-connection/settlementRegistry'
import {useEntityMutation} from '@github-ui/optimistic-connection/useEntityMutation'
import {useMutation} from '@github-ui/react-query'
import {
  updateIssueStateMutation as updateIssueStateQueryId,
  updateIssueStateMutationCloseMutation as updateIssueStateCloseQueryId,
} from '@github-ui/server-persisted-queries'
import type {UpdateIssueStateMutationMutation} from '@github-ui/server-persisted-queries/types/updateIssueStateMutation'
import type {
  IssueClosedStateReason as ServerIssueClosedStateReason,
  UpdateIssueStateMutationCloseMutationMutation,
} from '@github-ui/server-persisted-queries/types/updateIssueStateMutationCloseMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {commitLocalUpdate, type Environment, type RecordSourceProxy} from 'relay-runtime'

export function writeIssueStateToRelay(
  store: RecordSourceProxy,
  issue: {
    id: string
    state: 'OPEN' | 'CLOSED'
    stateReason: string | null
    closedAt: string | null
    duplicateOf?: {id: string; number: number; url: string} | null
  },
): void {
  const record = store.get(issue.id)
  if (!record) return
  record.setValue(issue.state, 'state')
  record.setValue(issue.stateReason, 'stateReason')
  record.setValue(issue.closedAt, 'closedAt')
  if (issue.duplicateOf) {
    const duplicate = store.get(issue.duplicateOf.id) ?? store.create(issue.duplicateOf.id, 'Issue')
    duplicate.setValue(issue.duplicateOf.number, 'number')
    duplicate.setValue(issue.duplicateOf.url, 'url')
    record.setLinkedRecord(duplicate, 'duplicateOf')
  } else {
    record.setValue(null, 'duplicateOf')
  }
}

export function adjustParentSummaryInRelay(
  store: RecordSourceProxy,
  parentIssueId: string,
  completedDelta: number,
): void {
  const summary = store.get(parentIssueId)?.getLinkedRecord('subIssuesSummary')
  if (!summary) return
  const total = summary?.getValue('total')
  const completed = summary?.getValue('completed')
  if (typeof total !== 'number' || typeof completed !== 'number') return
  summary.setValue(Math.max(0, Math.min(total, completed + completedDelta)), 'completed')
}

export function writeParentSummaryToRelay(store: RecordSourceProxy, parent: ReopenParent): void {
  const parentRecord = store.get(parent.id) ?? store.create(parent.id, 'Issue')
  const summary =
    parentRecord.getLinkedRecord('subIssuesSummary') ??
    store.create(`${parent.id}:subIssuesSummary:native-reopen`, 'SubIssuesSummary')
  summary.setValue(parent.subIssuesSummary.total, 'total')
  summary.setValue(parent.subIssuesSummary.completed, 'completed')
  parentRecord.setLinkedRecord(summary, 'subIssuesSummary')
}

export function toIssueStateOperation(issue: {
  id: string
  state: string
  stateReason?: string | null
  closedAt?: string | null
}): SetFieldsOperation {
  return {
    type: 'setFields',
    id: issue.id,
    fields: {
      state: issue.state,
      stateReason: issue.stateReason ?? null,
      closed: issue.state === 'CLOSED',
      closedAt: issue.state === 'CLOSED' ? issue.closedAt : null,
    },
  }
}

type IssueStateCallbacks<T> = {
  onError?: (error: Error) => void
  onCompleted?: (response: T) => void
}

type ReopenIssueCallbacks = IssueStateCallbacks<UpdateIssueStateMutationMutation>

export type ReopenIssue = (issueId: string, parentIssueId?: string, callbacks?: ReopenIssueCallbacks) => void

type CloseIssueDuplicate = {
  id: string
  number: number
  url: string
  repository: {name: string; owner: {login: string}}
}

export type IssueClosedStateReason = ServerIssueClosedStateReason

type CloseIssueInput = {
  issueId: string
  newStateReason: IssueClosedStateReason
  duplicateIssue?: CloseIssueDuplicate
  parentIssueId?: string
}

type CloseIssueCallbacks = IssueStateCallbacks<UpdateIssueStateMutationCloseMutationMutation>

export type CloseIssue = (input: CloseIssueInput, callbacks?: CloseIssueCallbacks) => void

type ReopenParent = {
  readonly id: string
  readonly subIssuesSummary: {readonly total: number; readonly completed: number}
}

type MutationResponse<T> = {
  data?: T | null
  errors?: ReadonlyArray<{message?: string}> | null
}

type ParentVariables<T> = {
  operation: DeltaEntityFieldValuesOperation
  parentIssueId: string
  result: Promise<T>
}

function reopenIssueOperation(
  issue: Parameters<typeof toIssueStateOperation>[0] & {
    duplicateOf?: {id: string; number: number; url: string} | null
  },
): SetFieldsOperation {
  const operation = toIssueStateOperation(issue)
  return {...operation, fields: {...operation.fields, duplicateOf: issue.duplicateOf ?? null}}
}

function closeIssueOperation(
  issueId: string,
  stateReason: IssueClosedStateReason,
  closedAt: string,
  duplicateIssue?: CloseIssueDuplicate,
): SetFieldsOperation {
  const operation = toIssueStateOperation({id: issueId, state: 'CLOSED', stateReason, closedAt})
  return {
    ...operation,
    fields: {...operation.fields, duplicateOf: duplicateIssue ?? null},
  }
}

function mergeCloseIssueDuplicate(
  confirmedDuplicate: {id: string; number: number; url: string} | null,
  selectedDuplicate?: CloseIssueDuplicate | null,
): CloseIssueDuplicate | null {
  if (!confirmedDuplicate) return selectedDuplicate ?? null
  if (confirmedDuplicate.id !== selectedDuplicate?.id) return null
  return {...selectedDuplicate, number: confirmedDuplicate.number, url: confirmedDuplicate.url}
}

function parentOperation(parentIssueId: string, completedDelta: number): DeltaEntityFieldValuesOperation {
  return {
    type: 'deltaEntityFieldValues',
    id: parentIssueId,
    field: 'subIssuesSummary',
    deltas: {completed: completedDelta},
  }
}

function useParentSummaryMutation<T>(getParent: (response: T) => ReopenParent | null | undefined) {
  return useMutation<ReopenParent, Error, ParentVariables<T>>({
    mutationKey: entityFieldsMutationKey(),
    mutationFn: async ({parentIssueId, result}) => {
      const parent = getParent(await result)
      if (!parent || parent.id !== parentIssueId) {
        throw new Error('Parent sub-issue summary was missing from the response')
      }
      return parent
    },
    onSuccess: parent =>
      notifySettlement(
        {type: 'setFields', id: parent.id, fields: {subIssuesSummary: parent.subIssuesSummary}},
        ENTITY_FIELDS_OWNER,
        DOCUMENT_FIELDS_PATH,
      ),
  })
}

async function readMutationResponse<T>(response: Response): Promise<MutationResponse<T> | undefined> {
  try {
    return (await response.json()) as MutationResponse<T>
  } catch {
    return undefined
  }
}

async function reopenIssueRequest({
  owner,
  repo,
  issueNumber,
  issueId,
}: {
  owner: string
  repo: string
  issueNumber: number
  issueId: string
}): Promise<UpdateIssueStateMutationMutation> {
  const query = new URLSearchParams({queryId: updateIssueStateQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/state.json?${query}`,
    {method: 'PATCH', body: {state: 'OPEN'}},
  )

  const result = await readMutationResponse<UpdateIssueStateMutationMutation>(response)

  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const issue = data?.reopenIssue?.issue
  // A valid issue confirms the write even when a nullable related selection (parent or duplicateOf)
  // produced a GraphQL error. This preserves the existing state-mutation partial-success contract.
  const stateMismatch = issue?.state !== 'OPEN' || issue.stateReason !== 'REOPENED' || issue.closedAt !== null
  if (!response.ok || !data || !issue || issue.id !== issueId || stateMismatch) {
    throw new Error(error ?? 'Failed to reopen issue')
  }
  return data
}

async function notifyMutationResult<T>(
  mutation: Promise<T>,
  fallbackErrorMessage: string,
  callbacks?: IssueStateCallbacks<T>,
): Promise<void> {
  let response: T
  try {
    response = await mutation
  } catch (error) {
    callbacks?.onError?.(error instanceof Error ? error : new Error(fallbackErrorMessage))
    return
  }
  callbacks?.onCompleted?.(response)
}

async function closeIssueRequest({
  owner,
  repo,
  issueNumber,
  issueId,
  stateReason,
  optimisticClosedAt,
  duplicateIssue,
}: {
  owner: string
  repo: string
  issueNumber: number
  issueId: string
  stateReason: IssueClosedStateReason
  optimisticClosedAt: string
  duplicateIssue?: CloseIssueDuplicate
}): Promise<UpdateIssueStateMutationCloseMutationMutation> {
  const query = new URLSearchParams({queryId: updateIssueStateCloseQueryId})
  const response = await reactFetchJSON(
    `/${encodeURIComponent(owner)}/${encodeURIComponent(repo)}/issues/${issueNumber}/state.json?${query}`,
    {method: 'PATCH', body: {state: 'CLOSED', stateReason, duplicateIssueId: duplicateIssue?.id}},
  )

  const result = await readMutationResponse<UpdateIssueStateMutationCloseMutationMutation>(response)

  const error = result?.errors?.find(item => item.message)?.message
  const data = result?.data
  const issue = data?.closeIssue?.issue
  const stateMismatch = issue?.state !== 'CLOSED' || issue.stateReason !== stateReason
  if (!response.ok || !data || !issue || issue.id !== issueId || stateMismatch) {
    throw new Error(error ?? 'Failed to close issue')
  }

  return {
    ...data,
    closeIssue: {
      ...data.closeIssue,
      issue: {
        ...issue,
        closedAt: issue.closedAt ?? optimisticClosedAt,
        duplicateOf: mergeCloseIssueDuplicate(issue.duplicateOf, duplicateIssue),
      },
    },
  }
}

async function awaitIssueAndParent<T>(issueMutation: Promise<T>, parentMutation?: Promise<ReopenParent>): Promise<T> {
  try {
    return await issueMutation
  } finally {
    try {
      await parentMutation
    } catch {
      // Missing parent data must not turn a confirmed issue state change into a failure.
    }
  }
}

export function useCloseIssueMutation({
  owner,
  repo,
  issueNumber,
  environment,
}: {
  owner: string
  repo: string
  issueNumber: number
  environment: Environment
}): CloseIssue {
  const {mutateAsync: mutateIssue} = useEntityMutation<UpdateIssueStateMutationCloseMutationMutation>({
    scope: {id: `issue-state:${owner}/${repo}#${issueNumber}`},
    mutationFn: operation => {
      const duplicateIssue = operation.fields.duplicateOf as CloseIssueDuplicate | null
      const stateReason = operation.fields.stateReason as IssueClosedStateReason
      return closeIssueRequest({
        owner,
        repo,
        issueNumber,
        issueId: operation.id,
        stateReason,
        optimisticClosedAt: operation.fields.closedAt as string,
        duplicateIssue: duplicateIssue ?? undefined,
      })
    },
    getConfirmedOperation: (data, operation) => {
      const issue = data.closeIssue?.issue
      const selectedDuplicate = operation.fields.duplicateOf as CloseIssueDuplicate | null
      return issue
        ? closeIssueOperation(
            issue.id,
            issue.stateReason as IssueClosedStateReason,
            issue.closedAt ?? '',
            mergeCloseIssueDuplicate(issue.duplicateOf, selectedDuplicate) ?? undefined,
          )
        : undefined
    },
    onSuccess: () => decrementIssuesTabCounter(),
  })
  const {mutateAsync: mutateParentSummary} = useParentSummaryMutation(
    (response: UpdateIssueStateMutationCloseMutationMutation) => response.closeIssue?.issue?.parent,
  )

  return useCallback(
    (input, callbacks) => {
      const optimisticClosedAt = new Date().toISOString()
      const operation = closeIssueOperation(
        input.issueId,
        input.newStateReason,
        optimisticClosedAt,
        input.duplicateIssue,
      )
      // RELAY-CLEANUP(IssueActions): mirror native close state until IssueActions and retained
      // issue-list counts no longer read Relay.
      const relayUpdate = environment.applyUpdate({
        storeUpdater: store => {
          writeIssueStateToRelay(store, {
            id: input.issueId,
            state: 'CLOSED',
            stateReason: input.newStateReason,
            closedAt: optimisticClosedAt,
            duplicateOf: input.duplicateIssue,
          })
          if (input.parentIssueId) adjustParentSummaryInRelay(store, input.parentIssueId, 1)
          adjustOpenClosedCounts(store, -1)
        },
      })
      const relayCallbacks: CloseIssueCallbacks = {
        onCompleted: response => {
          const issue = response.closeIssue?.issue
          try {
            commitLocalUpdate(environment, store => {
              if (issue) writeIssueStateToRelay(store, issue)
              if (issue?.parent) writeParentSummaryToRelay(store, issue.parent)
            })
          } finally {
            relayUpdate.dispose()
          }
          commitLocalUpdate(environment, store => adjustOpenClosedCounts(store, -1))
          callbacks?.onCompleted?.(response)
        },
        onError: error => {
          relayUpdate.dispose()
          callbacks?.onError?.(error)
        },
      }
      const issueMutation = mutateIssue({operation})
      const parentMutation = input.parentIssueId
        ? mutateParentSummary({
            operation: parentOperation(input.parentIssueId, 1),
            parentIssueId: input.parentIssueId,
            result: issueMutation,
          })
        : undefined
      void notifyMutationResult(
        awaitIssueAndParent(issueMutation, parentMutation),
        'Failed to close issue',
        relayCallbacks,
      )
    },
    [environment, mutateIssue, mutateParentSummary],
  )
}

export function useReopenIssueMutation({
  owner,
  repo,
  issueNumber,
  environment,
}: {
  owner: string
  repo: string
  issueNumber: number
  environment: Environment
}): ReopenIssue {
  const {mutateAsync: mutateIssue} = useEntityMutation<UpdateIssueStateMutationMutation>({
    scope: {id: `issue-state:${owner}/${repo}#${issueNumber}`},
    mutationFn: operation => reopenIssueRequest({owner, repo, issueNumber, issueId: operation.id}),
    getConfirmedOperation: data => {
      const issue = data.reopenIssue?.issue
      return issue ? reopenIssueOperation(issue) : undefined
    },
    onSuccess: () => incrementIssuesTabCounter(),
  })
  const {mutateAsync: mutateParentSummary} = useParentSummaryMutation(
    (response: UpdateIssueStateMutationMutation) => response.reopenIssue?.issue?.parent,
  )

  return useCallback(
    (issueId, parentIssueId, callbacks) => {
      // RELAY-CLEANUP(IssueActions): mirror native reopen state until IssueActions and retained
      // issue-list counts no longer read Relay.
      const relayUpdate = environment.applyUpdate({
        storeUpdater: store => {
          writeIssueStateToRelay(store, {
            id: issueId,
            state: 'OPEN',
            stateReason: 'REOPENED',
            closedAt: null,
          })
          if (parentIssueId) adjustParentSummaryInRelay(store, parentIssueId, -1)
          adjustOpenClosedCounts(store, 1)
        },
      })
      const relayCallbacks: ReopenIssueCallbacks = {
        onCompleted: response => {
          const issue = response.reopenIssue?.issue
          try {
            commitLocalUpdate(environment, store => {
              if (issue) writeIssueStateToRelay(store, issue)
              if (issue?.parent) writeParentSummaryToRelay(store, issue.parent)
            })
          } finally {
            relayUpdate.dispose()
          }
          commitLocalUpdate(environment, store => adjustOpenClosedCounts(store, 1))
          callbacks?.onCompleted?.(response)
        },
        onError: error => {
          relayUpdate.dispose()
          callbacks?.onError?.(error)
        },
      }
      const issueMutation = mutateIssue({
        operation: reopenIssueOperation({
          id: issueId,
          state: 'OPEN',
          stateReason: 'REOPENED',
          closedAt: null,
          duplicateOf: null,
        }),
      })
      const parentMutation = parentIssueId
        ? mutateParentSummary({
            operation: parentOperation(parentIssueId, -1),
            parentIssueId,
            result: issueMutation,
          })
        : undefined
      void notifyMutationResult(
        awaitIssueAndParent(issueMutation, parentMutation),
        'Failed to reopen issue',
        relayCallbacks,
      )
    },
    [environment, mutateIssue, mutateParentSummary],
  )
}
